import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import PortalLayout from "@/components/PortalLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/hooks/use-toast";
import { MOCK_VESSELS, resolveClientKey, getClientVessels } from "@/data/portalMockData";
import { usePortalCompany } from "@/hooks/usePortalCompany";
import { AIRCAT35_SYSTEMS } from "@/data/aircatPmsComponentTree";
import { format, isBefore, startOfDay } from "date-fns";
import {
  Wrench, AlertTriangle, Calendar, Search, Download, Ship, ChevronRight, ChevronDown,
  ArrowLeft, Cog, ClipboardList, FileText, Activity, CheckCircle2,
  Clock, BarChart3, Shield, Package, Bug, History, FolderTree,
  Gauge, AlertCircle, TrendingUp, Eye, CircleDot
} from "lucide-react";

// ─── Status Colors (portal design tokens) ──────────────────────────────────
const STATUS_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  scheduled: { bg: "bg-blue-50", text: "text-blue-700", label: "Scheduled" },
  assigned: { bg: "bg-blue-50", text: "text-blue-700", label: "Assigned" },
  in_progress: { bg: "bg-amber-50", text: "text-amber-700", label: "In Progress" },
  overdue: { bg: "bg-red-50", text: "text-red-700", label: "Overdue" },
  completed: { bg: "bg-green-50", text: "text-green-700", label: "Completed" },
  deferred: { bg: "bg-purple-50", text: "text-purple-700", label: "Deferred" },
};

const PRIORITY_COLORS: Record<string, string> = {
  critical: "bg-red-100 text-red-700",
  high: "bg-orange-100 text-orange-700",
  medium: "bg-amber-100 text-amber-700",
  low: "bg-green-100 text-green-700",
};

const drydockSchedule = [
  { vessel: "Velocity Pioneer", specialSurvey: "2028-06-15", intermediate: "2026-12-01", annual: "2025-06-15", docking: "2026-06-15" },
  { vessel: "Velocity Endeavour", specialSurvey: "2028-08-20", intermediate: "2026-08-20", annual: "2025-08-20", docking: "2026-08-20" },
  { vessel: "Velocity Kaieteur", specialSurvey: "2029-01-10", intermediate: "2027-01-10", annual: "2025-07-10", docking: "2027-01-10" },
  { vessel: "Velocity Rupununi", specialSurvey: "2029-03-15", intermediate: "2027-03-15", annual: "2025-09-15", docking: "2027-03-15" },
  { vessel: "Velocity Essequibo", specialSurvey: "2029-05-20", intermediate: "2027-05-20", annual: "2025-11-20", docking: "2027-05-20" },
  { vessel: "Velocity Benguela", specialSurvey: "2029-01-10", intermediate: "2027-01-10", annual: "2025-07-10", docking: "2027-01-10" },
  { vessel: "Velocity Forcados", specialSurvey: "2029-06-15", intermediate: "2027-06-15", annual: "2025-12-15", docking: "2027-06-15" },
  { vessel: "Velocity Bonny", specialSurvey: "2029-08-20", intermediate: "2027-08-20", annual: "2026-02-20", docking: "2027-08-20" },
];

const daysUntil = (dateStr: string) => Math.ceil((new Date(dateStr).getTime() - Date.now()) / 86400000);
const ragForDays = (d: number) => d <= 30 ? "text-[#DC3545]" : d <= 90 ? "text-[#FFC107]" : "text-[#28A745]";

function getJobStatus(job: any): string {
  if (job.status === "completed") return "completed";
  if (job.status === "deferred") return "deferred";
  if (job.status === "in_progress") return "in_progress";
  if (job.status === "overdue" || (job.next_due_date && isBefore(new Date(job.next_due_date), startOfDay(new Date())))) return "overdue";
  if (job.assigned_to) return "assigned";
  return "scheduled";
}

const MaintenancePMS = () => {
  const { companyName } = usePortalCompany();
  const clientKey = resolveClientKey(companyName);
  const clientVessels = clientKey ? getClientVessels(clientKey) : MOCK_VESSELS;
  const clientDrydock = drydockSchedule.filter(d => clientVessels.some(v => v.name === d.vessel));

  const [selectedVessel, setSelectedVessel] = useState<any>(null);
  const [vesselTab, setVesselTab] = useState("dashboard");
  const [search, setSearch] = useState("");

  // ─── Real PMS Data from DB ──────────────────────────────────────────────
  const { data: dbVessels = [] } = useQuery({
    queryKey: ["portal-fleet-vessels"],
    queryFn: async () => { const { data } = await supabase.from("fleet_vessels").select("*").order("name"); return data ?? []; },
  });
  const { data: allEquipment = [] } = useQuery({
    queryKey: ["portal-pms-equipment"],
    queryFn: async () => { const { data } = await supabase.from("pms_equipment").select("*").eq("is_active", true).order("vessel_name,category,equipment_name"); return data ?? []; },
  });
  const { data: allJobs = [] } = useQuery({
    queryKey: ["portal-pms-jobs"],
    queryFn: async () => { const { data } = await supabase.from("pms_jobs").select("*").order("next_due_date"); return data ?? []; },
  });
  const { data: allWorkOrders = [] } = useQuery({
    queryKey: ["portal-pms-work-orders"],
    queryFn: async () => { const { data } = await supabase.from("pms_work_orders").select("*").order("created_at", { ascending: false }); return data ?? []; },
  });
  const { data: allDefects = [] } = useQuery({
    queryKey: ["portal-pms-defects"],
    queryFn: async () => { const { data } = await supabase.from("pms_defects").select("*").order("reported_date", { ascending: false }); return data ?? []; },
  });

  // Match portal vessels to DB vessels by name (case-insensitive)
  const getDbVessel = (mockVessel: any) => {
    return dbVessels.find((v: any) => v.name.toLowerCase() === mockVessel.name.toLowerCase());
  };

  // Stats per vessel
  const vesselStats = useMemo(() => {
    const stats = new Map<string, { equipment: number; jobs: number; overdue: number; completed: number; inProgress: number; defects: number; systems: Set<string> }>();
    clientVessels.forEach(v => {
      const dbV = getDbVessel(v);
      const vesselId = dbV?.id;
      const eqCount = vesselId ? allEquipment.filter((e: any) => e.vessel_id === vesselId).length : 0;
      const vesselJobs = vesselId ? allJobs.filter((j: any) => j.vessel_id === vesselId) : [];
      const overdue = vesselJobs.filter(j => getJobStatus(j) === "overdue").length;
      const completed = vesselJobs.filter(j => j.status === "completed").length;
      const inProgress = vesselJobs.filter(j => j.status === "in_progress").length;
      const vesselDefects = vesselId ? allDefects.filter((d: any) => d.vessel_id === vesselId && d.status === "open").length : 0;
      const systems = new Set<string>();
      if (vesselId) allEquipment.filter((e: any) => e.vessel_id === vesselId && e.system_name && !e.parent_equipment_id).forEach((e: any) => systems.add(e.system_name));
      stats.set(v.id, { equipment: eqCount, jobs: vesselJobs.length, overdue, completed, inProgress, defects: vesselDefects, systems });
    });
    return stats;
  }, [clientVessels, dbVessels, allEquipment, allJobs, allDefects]);

  // Selected vessel PMS data
  const selectedDbVessel = selectedVessel ? getDbVessel(selectedVessel) : null;
  const selectedVesselId = selectedDbVessel?.id;
  const vesselEquipment = useMemo(() => selectedVesselId ? allEquipment.filter((e: any) => e.vessel_id === selectedVesselId) : [], [selectedVesselId, allEquipment]);
  const vesselJobs = useMemo(() => selectedVesselId ? allJobs.filter((j: any) => j.vessel_id === selectedVesselId) : [], [selectedVesselId, allJobs]);
  const vesselWorkOrders = useMemo(() => selectedVesselId ? allWorkOrders.filter((w: any) => w.vessel_id === selectedVesselId) : [], [selectedVesselId, allWorkOrders]);
  const vesselDefects = useMemo(() => selectedVesselId ? allDefects.filter((d: any) => d.vessel_id === selectedVesselId) : [], [selectedVesselId, allDefects]);

  // Equipment tree grouped by system_name (19-system hierarchy)
  const equipmentTree = useMemo(() => {
    const systemGrouped = new Map<string, any[]>();
    vesselEquipment.forEach((e: any) => {
      const sysName = e.system_name || e.category || "Uncategorised";
      if (!systemGrouped.has(sysName)) systemGrouped.set(sysName, []);
      systemGrouped.get(sysName)!.push(e);
    });
    return systemGrouped;
  }, [vesselEquipment]);

  // ─── Fleet total overdue ──────────────────────────────────────────────
  const totalOverdue = useMemo(() => {
    let count = 0;
    vesselStats.forEach(s => count += s.overdue);
    return count;
  }, [vesselStats]);

  // ═════════════════════════════════════════════════════════════════════════
  // ─── VESSEL GRID ─────────────────────────────────────────────────────────
  // ═════════════════════════════════════════════════════════════════════════
  const renderVesselGrid = () => (
    <div>
      {totalOverdue > 0 && (
        <div className="portal-card p-3 mb-4 border-l-4 border-[#DC3545] bg-red-50 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-[#DC3545]" />
          <p className="text-sm font-medium text-[#DC3545]">{totalOverdue} overdue maintenance item(s) across the fleet</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {clientVessels.map(v => {
          const s = vesselStats.get(v.id);
          const hasData = (s?.equipment || 0) > 0;
          return (
            <button key={v.id} onClick={() => { setSelectedVessel(v); setVesselTab("dashboard"); }}
              className="portal-card p-5 text-left hover:shadow-lg hover:border-[hsl(211,100%,40%)]/30 transition-all group">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <Ship className="w-5 h-5 text-[hsl(211,100%,40%)]" />
                  <div>
                    <h3 className="text-sm font-bold text-[hsl(215,25%,17%)] group-hover:text-[hsl(211,100%,40%)]">{v.name}</h3>
                    <p className="text-[10px] text-[hsl(215,12%,50%)]">{v.type}</p>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-[hsl(215,12%,50%)] group-hover:text-[hsl(211,100%,40%)] transition-colors" />
              </div>

              {hasData ? (
                <div className="grid grid-cols-3 gap-3 mt-3">
                  <div className="text-center p-2 bg-[hsl(220,20%,97%)] rounded-lg">
                    <p className="text-lg font-bold text-[hsl(215,25%,17%)]">{s?.equipment || 0}</p>
                    <p className="text-[9px] text-[hsl(215,12%,50%)]">Equipment</p>
                  </div>
                  <div className="text-center p-2 bg-[hsl(220,20%,97%)] rounded-lg">
                    <p className="text-lg font-bold text-[hsl(215,25%,17%)]">{s?.jobs || 0}</p>
                    <p className="text-[9px] text-[hsl(215,12%,50%)]">Jobs</p>
                  </div>
                  <div className="text-center p-2 bg-[hsl(220,20%,97%)] rounded-lg">
                    <p className={`text-lg font-bold ${(s?.overdue || 0) > 0 ? "text-[#DC3545]" : "text-[#28A745]"}`}>{s?.overdue || 0}</p>
                    <p className="text-[9px] text-[hsl(215,12%,50%)]">Overdue</p>
                  </div>
                </div>
              ) : (
                <div className="mt-3 p-3 bg-[hsl(220,20%,97%)] rounded-lg text-center">
                  <p className="text-[11px] text-[hsl(215,12%,50%)]">PMS data pending setup</p>
                </div>
              )}

              {(s?.defects || 0) > 0 && (
                <div className="mt-2 flex items-center gap-1.5 text-[10px] text-[#DC3545]">
                  <Bug className="w-3 h-3" /> {s?.defects} open defect(s)
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Drydock & Survey Schedule */}
      {clientDrydock.length > 0 && (
        <div>
          <h2 className="text-lg font-bold text-[hsl(215,25%,17%)] mb-3 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[hsl(211,100%,40%)]" /> Drydock & Survey Schedule
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {clientDrydock.map(ds => (
              <div key={ds.vessel} className="portal-card p-5">
                <h3 className="font-bold text-sm mb-3 text-[hsl(215,25%,17%)]">{ds.vessel}</h3>
                <div className="space-y-2 text-xs">
                  {[
                    { label: "Special Survey", date: ds.specialSurvey },
                    { label: "Intermediate Survey", date: ds.intermediate },
                    { label: "Annual Survey", date: ds.annual },
                    { label: "Next Docking", date: ds.docking },
                  ].map(item => {
                    const d = daysUntil(item.date);
                    return (
                      <div key={item.label} className="flex items-center justify-between py-1.5 border-b border-[hsl(220,15%,92%)] last:border-0">
                        <span className="text-[hsl(215,12%,50%)]">{item.label}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-[hsl(215,25%,17%)]">{item.date}</span>
                          <span className={`font-bold ${ragForDays(d)}`}>{d}d</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // ═════════════════════════════════════════════════════════════════════════
  // ─── VESSEL DETAIL: DASHBOARD ────────────────────────────────────────────
  // ═════════════════════════════════════════════════════════════════════════
  const renderVesselDashboard = () => {
    const overdueJobs = vesselJobs.filter(j => getJobStatus(j) === "overdue");
    const upcomingJobs = vesselJobs.filter(j => getJobStatus(j) === "scheduled" || getJobStatus(j) === "assigned").slice(0, 5);
    const completedCount = vesselJobs.filter(j => j.status === "completed").length;
    const totalJobs = vesselJobs.length;
    const completionRate = totalJobs > 0 ? Math.round((completedCount / totalJobs) * 100) : 0;
    const systemCount = equipmentTree.size;
    const openDefects = vesselDefects.filter((d: any) => d.status === "open").length;

    return (
      <div className="space-y-4">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Equipment", value: vesselEquipment.length, icon: Cog, color: "text-[hsl(211,100%,40%)]" },
            { label: "Systems", value: systemCount, icon: FolderTree, color: "text-[hsl(211,100%,40%)]" },
            { label: "Planned Jobs", value: totalJobs, icon: ClipboardList, color: "text-[hsl(211,100%,40%)]" },
            { label: "Overdue", value: overdueJobs.length, icon: AlertTriangle, color: overdueJobs.length > 0 ? "text-[#DC3545]" : "text-[#28A745]" },
          ].map(kpi => (
            <Card key={kpi.label} className="portal-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <kpi.icon className={`w-4 h-4 ${kpi.color}`} />
                  <span className="text-[10px] text-[hsl(215,12%,50%)] uppercase tracking-wider font-medium">{kpi.label}</span>
                </div>
                <p className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Completion Rate + Open Defects */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="portal-card">
            <CardContent className="p-5">
              <h4 className="text-xs font-bold text-[hsl(215,25%,17%)] mb-3">Job Completion Rate</h4>
              <div className="flex items-center gap-3">
                <Progress value={completionRate} className="h-2.5 flex-1" />
                <span className="text-sm font-bold text-[hsl(215,25%,17%)]">{completionRate}%</span>
              </div>
              <p className="text-[10px] text-[hsl(215,12%,50%)] mt-2">{completedCount} of {totalJobs} jobs completed</p>
            </CardContent>
          </Card>
          <Card className="portal-card">
            <CardContent className="p-5">
              <h4 className="text-xs font-bold text-[hsl(215,25%,17%)] mb-3">Open Defects</h4>
              <p className={`text-2xl font-bold ${openDefects > 0 ? "text-[#DC3545]" : "text-[#28A745]"}`}>{openDefects}</p>
              <p className="text-[10px] text-[hsl(215,12%,50%)] mt-1">{vesselDefects.length} total defect records</p>
            </CardContent>
          </Card>
        </div>

        {/* Overdue Items */}
        {overdueJobs.length > 0 && (
          <Card className="portal-card border-l-4 border-[#DC3545]">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 text-[#DC3545]">
                <AlertTriangle className="w-4 h-4" /> Overdue Maintenance ({overdueJobs.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {overdueJobs.map((j: any) => (
                  <div key={j.id} className="flex items-center justify-between p-2.5 bg-red-50 rounded-lg text-xs">
                    <div>
                      <p className="font-medium text-[hsl(215,25%,17%)]">{j.title}</p>
                      <p className="text-[10px] text-[hsl(215,12%,50%)]">{j.job_number} · {j.department}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] text-[#DC3545] font-bold">Due: {j.next_due_date}</p>
                      <Badge className={`text-[8px] ${PRIORITY_COLORS[j.priority] || ""}`}>{j.priority}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Upcoming Jobs */}
        {upcomingJobs.length > 0 && (
          <Card className="portal-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Clock className="w-4 h-4 text-[hsl(211,100%,40%)]" /> Upcoming Maintenance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {upcomingJobs.map((j: any) => {
                  const status = getJobStatus(j);
                  const sc = STATUS_COLORS[status] || STATUS_COLORS.scheduled;
                  return (
                    <div key={j.id} className="flex items-center justify-between p-2.5 bg-[hsl(220,20%,97%)] rounded-lg text-xs">
                      <div>
                        <p className="font-medium text-[hsl(215,25%,17%)]">{j.title}</p>
                        <p className="text-[10px] text-[hsl(215,12%,50%)]">{j.job_number} · {j.department}{j.assigned_to_name ? ` · ${j.assigned_to_name}` : ""}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-[hsl(215,12%,50%)]">{j.next_due_date}</span>
                        <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${sc.bg} ${sc.text}`}>{sc.label}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {vesselEquipment.length === 0 && (
          <Card className="portal-card">
            <CardContent className="p-8 text-center">
              <Cog className="w-8 h-8 text-[hsl(215,12%,50%)] mx-auto mb-3" />
              <p className="text-sm text-[hsl(215,12%,50%)]">PMS data is being configured for this vessel</p>
              <p className="text-[11px] text-[hsl(215,12%,50%)]/60 mt-1">Equipment and maintenance schedules will appear here once set up</p>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  // ═════════════════════════════════════════════════════════════════════════
  // ─── VESSEL DETAIL: EQUIPMENT TREE ───────────────────────────────────────
  // ═════════════════════════════════════════════════════════════════════════
  const [expandedSystems, setExpandedSystems] = useState<Set<string>>(new Set());

  const renderEquipmentTree = () => {
    if (vesselEquipment.length === 0) return (
      <Card className="portal-card"><CardContent className="p-8 text-center"><p className="text-sm text-[hsl(215,12%,50%)]">No equipment registered</p></CardContent></Card>
    );

    const toggleSystem = (sysId: string) => setExpandedSystems(prev => { const n = new Set(prev); n.has(sysId) ? n.delete(sysId) : n.add(sysId); return n; });

    const renderNode = (eq: any, depth: number = 0) => {
      const children = vesselEquipment.filter((e: any) => e.parent_equipment_id === eq.id);
      const isExpanded = expandedSystems.has(eq.id);
      const matchesSearch = !search || eq.equipment_name.toLowerCase().includes(search.toLowerCase());
      const childMatches = children.some((c: any) => c.equipment_name.toLowerCase().includes(search.toLowerCase()));
      if (search && !matchesSearch && !childMatches) return null;

      return (
        <div key={eq.id} className={depth > 0 ? "ml-4 border-l border-[hsl(220,15%,90%)]" : ""}>
          <button className="w-full flex items-center gap-2 p-2 pl-3 text-left hover:bg-[hsl(220,20%,97%)] transition-colors"
            onClick={() => children.length > 0 ? toggleSystem(eq.id) : null}>
            {children.length > 0
              ? (isExpanded || (search && childMatches) ? <ChevronDown className="w-3 h-3 text-[hsl(215,12%,50%)]" /> : <ChevronRight className="w-3 h-3 text-[hsl(215,12%,50%)]" />)
              : <CircleDot className="w-3 h-3 text-[hsl(215,12%,50%)]/30" />}
            <Cog className="w-3 h-3 text-[hsl(215,12%,50%)]" />
            <span className="text-xs text-[hsl(215,25%,17%)] flex-1 truncate">{eq.equipment_name}</span>
            {eq.manufacturer && <span className="text-[9px] text-[hsl(215,12%,50%)]">{eq.manufacturer}{eq.model ? ` ${eq.model}` : ""}</span>}
            {eq.criticality && (
              <Badge variant="outline" className={`text-[8px] ${eq.criticality === "A" ? "border-red-300 text-red-600" : eq.criticality === "B" ? "border-amber-300 text-amber-600" : "border-green-300 text-green-600"}`}>
                {eq.criticality}
              </Badge>
            )}
            {eq.class_survey_item && <Shield className="w-3 h-3 text-blue-500" />}
          </button>
          {(isExpanded || (search && childMatches)) && children.length > 0 && (
            <div className="pb-1">{children.map((c: any) => renderNode(c, depth + 1))}</div>
          )}
        </div>
      );
    };

    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 mb-3">
          <Input placeholder="Search equipment..." value={search} onChange={e => setSearch(e.target.value)}
            className="max-w-sm h-8 text-xs border-[hsl(220,15%,90%)]" />
          <Badge variant="outline" className="text-[10px]">{vesselEquipment.length} components</Badge>
        </div>

        {AIRCAT35_SYSTEMS.map(sys => {
          const sysEq = equipmentTree.get(sys.name);
          if (!sysEq || sysEq.length === 0) return null;
          const rootEq = sysEq.filter((e: any) => !e.parent_equipment_id || !sysEq.some((p: any) => p.id === e.parent_equipment_id));
          const isExpanded = expandedSystems.has(sys.id) || !!search;
          const classItems = sysEq.filter((e: any) => e.class_survey_item).length;

          return (
            <div key={sys.id} className="portal-card overflow-hidden">
              <button className="w-full flex items-center gap-3 p-3 text-left hover:bg-[hsl(220,20%,97%)] transition-colors"
                onClick={() => toggleSystem(sys.id)}>
                <ChevronRight className={`w-4 h-4 text-[hsl(215,12%,50%)] transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                <span className="text-sm">{sys.icon}</span>
                <span className="text-xs font-bold text-[hsl(215,25%,17%)] flex-1">{sys.name}</span>
                <Badge variant="outline" className="text-[9px]">{sysEq.length}</Badge>
                {classItems > 0 && <Badge variant="outline" className="text-[8px] border-blue-300 text-blue-600"><Shield className="w-2.5 h-2.5 mr-0.5" />{classItems}</Badge>}
              </button>
              {isExpanded && (
                <div className="pb-2 border-t border-[hsl(220,15%,92%)]">
                  {rootEq.map((eq: any) => renderNode(eq))}
                </div>
              )}
            </div>
          );
        }).filter(Boolean)}

        {/* Render any equipment not matching a known system */}
        {Array.from(equipmentTree.entries())
          .filter(([sysName]) => !AIRCAT35_SYSTEMS.some(s => s.name === sysName))
          .map(([sysName, eqList]) => {
            const rootEq = eqList.filter((e: any) => !e.parent_equipment_id || !eqList.some((p: any) => p.id === e.parent_equipment_id));
            const isExpanded = expandedSystems.has(sysName) || !!search;
            return (
              <div key={sysName} className="portal-card overflow-hidden">
                <button className="w-full flex items-center gap-3 p-3 text-left hover:bg-[hsl(220,20%,97%)] transition-colors"
                  onClick={() => toggleSystem(sysName)}>
                  <ChevronRight className={`w-4 h-4 text-[hsl(215,12%,50%)] transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                  <span className="text-xs font-bold text-[hsl(215,25%,17%)] flex-1">{sysName}</span>
                  <Badge variant="outline" className="text-[9px]">{eqList.length}</Badge>
                </button>
                {isExpanded && (
                  <div className="pb-2 border-t border-[hsl(220,15%,92%)]">
                    {rootEq.map((eq: any) => renderNode(eq))}
                  </div>
                )}
              </div>
            );
          })}
      </div>
    );
  };

  // ═════════════════════════════════════════════════════════════════════════
  // ─── VESSEL DETAIL: MAINTENANCE SCHEDULE ─────────────────────────────────
  // ═════════════════════════════════════════════════════════════════════════
  const renderMaintenanceSchedule = () => {
    if (vesselJobs.length === 0) return (
      <Card className="portal-card"><CardContent className="p-8 text-center"><p className="text-sm text-[hsl(215,12%,50%)]">No maintenance jobs scheduled</p></CardContent></Card>
    );

    const sortedJobs = [...vesselJobs].sort((a: any, b: any) => {
      if (!a.next_due_date) return 1;
      if (!b.next_due_date) return -1;
      return new Date(a.next_due_date).getTime() - new Date(b.next_due_date).getTime();
    });

    return (
      <div>
        <div className="flex flex-wrap gap-3 mb-4">
          {Object.entries(STATUS_COLORS).map(([key, val]) => {
            const count = vesselJobs.filter(j => getJobStatus(j) === key).length;
            if (count === 0) return null;
            return (
              <div key={key} className="flex items-center gap-1.5">
                <span className={`w-3 h-3 rounded-sm ${val.bg}`} />
                <span className="text-[10px] text-[hsl(215,12%,50%)]">{val.label}</span>
                <span className="text-[10px] font-bold text-[hsl(215,25%,17%)]">{count}</span>
              </div>
            );
          })}
        </div>
        <div className="portal-card overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="border-b bg-[hsl(220,20%,97%)]">
              {["Job #", "Title", "Department", "Type", "Priority", "Due Date", "Assigned To", "Status"].map(h => (
                <th key={h} className="px-3 py-2.5 text-left font-medium text-[hsl(215,12%,50%)]">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {sortedJobs.map((j: any) => {
                const status = getJobStatus(j);
                const sc = STATUS_COLORS[status] || STATUS_COLORS.scheduled;
                return (
                  <tr key={j.id} className="border-b border-[hsl(220,15%,92%)] hover:bg-[hsl(220,20%,97%)]">
                    <td className="px-3 py-2.5 font-medium font-mono">{j.job_number}</td>
                    <td className="px-3 py-2.5 max-w-[200px] truncate">{j.title}</td>
                    <td className="px-3 py-2.5 capitalize">{j.department}</td>
                    <td className="px-3 py-2.5 capitalize">{j.job_type}</td>
                    <td className="px-3 py-2.5"><span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${PRIORITY_COLORS[j.priority] || ""}`}>{j.priority}</span></td>
                    <td className="px-3 py-2.5">{j.next_due_date || "—"}</td>
                    <td className="px-3 py-2.5">{j.assigned_to_name || <span className="text-[hsl(215,12%,50%)]/50">Unassigned</span>}</td>
                    <td className="px-3 py-2.5"><span className={`text-[9px] font-bold px-2 py-0.5 rounded-full uppercase ${sc.bg} ${sc.text}`}>{sc.label}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ═════════════════════════════════════════════════════════════════════════
  // ─── VESSEL DETAIL: WORK HISTORY ─────────────────────────────────────────
  // ═════════════════════════════════════════════════════════════════════════
  const renderWorkHistory = () => {
    const completedWOs = vesselWorkOrders.filter((w: any) => w.status === "completed" || w.status === "closed");
    const completedJobs = vesselJobs.filter((j: any) => j.status === "completed");

    if (completedWOs.length === 0 && completedJobs.length === 0) return (
      <Card className="portal-card"><CardContent className="p-8 text-center"><p className="text-sm text-[hsl(215,12%,50%)]">No completed work history</p></CardContent></Card>
    );

    return (
      <div className="space-y-4">
        {completedWOs.length > 0 && (
          <div>
            <h4 className="text-xs font-bold text-[hsl(215,25%,17%)] mb-2 flex items-center gap-2">
              <FileText className="w-4 h-4 text-[hsl(211,100%,40%)]" /> Completed Work Orders ({completedWOs.length})
            </h4>
            <div className="portal-card overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b bg-[hsl(220,20%,97%)]">
                  {["WO #", "Title", "Type", "Completed", "Actual Hours", "Findings"].map(h => (
                    <th key={h} className="px-3 py-2 text-left font-medium text-[hsl(215,12%,50%)]">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {completedWOs.slice(0, 20).map((w: any) => (
                    <tr key={w.id} className="border-b border-[hsl(220,15%,92%)]">
                      <td className="px-3 py-2 font-mono font-medium">{w.work_order_number}</td>
                      <td className="px-3 py-2 max-w-[200px] truncate">{w.title}</td>
                      <td className="px-3 py-2 capitalize">{w.work_type}</td>
                      <td className="px-3 py-2">{w.completed_at ? format(new Date(w.completed_at), "dd MMM yyyy") : "—"}</td>
                      <td className="px-3 py-2">{w.actual_hours || "—"}h</td>
                      <td className="px-3 py-2 max-w-[200px] truncate text-[hsl(215,12%,50%)]">{w.findings_summary || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {completedJobs.length > 0 && (
          <div>
            <h4 className="text-xs font-bold text-[hsl(215,25%,17%)] mb-2 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#28A745]" /> Completed Planned Jobs ({completedJobs.length})
            </h4>
            <div className="portal-card overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b bg-[hsl(220,20%,97%)]">
                  {["Job #", "Title", "Department", "Completed By", "Completed Date"].map(h => (
                    <th key={h} className="px-3 py-2 text-left font-medium text-[hsl(215,12%,50%)]">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {completedJobs.slice(0, 20).map((j: any) => (
                    <tr key={j.id} className="border-b border-[hsl(220,15%,92%)]">
                      <td className="px-3 py-2 font-mono font-medium">{j.job_number}</td>
                      <td className="px-3 py-2 max-w-[200px] truncate">{j.title}</td>
                      <td className="px-3 py-2 capitalize">{j.department}</td>
                      <td className="px-3 py-2">{j.last_completed_by_name || "—"}</td>
                      <td className="px-3 py-2">{j.last_completed_at ? format(new Date(j.last_completed_at), "dd MMM yyyy") : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ═════════════════════════════════════════════════════════════════════════
  // ─── VESSEL DETAIL: DEFECTS ──────────────────────────────────────────────
  // ═════════════════════════════════════════════════════════════════════════
  const renderDefects = () => {
    if (vesselDefects.length === 0) return (
      <Card className="portal-card"><CardContent className="p-8 text-center"><p className="text-sm text-[hsl(215,12%,50%)]">No defects reported</p></CardContent></Card>
    );

    return (
      <div className="portal-card overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="border-b bg-[hsl(220,20%,97%)]">
            {["Title", "Type", "Severity", "Reported", "Status", "Location"].map(h => (
              <th key={h} className="px-3 py-2.5 text-left font-medium text-[hsl(215,12%,50%)]">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {vesselDefects.map((d: any) => (
              <tr key={d.id} className="border-b border-[hsl(220,15%,92%)] hover:bg-[hsl(220,20%,97%)]">
                <td className="px-3 py-2.5 font-medium">{d.title}</td>
                <td className="px-3 py-2.5 capitalize">{d.defect_type}</td>
                <td className="px-3 py-2.5">
                  <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${d.severity === "critical" ? "bg-red-100 text-red-700" : d.severity === "major" ? "bg-orange-100 text-orange-700" : "bg-amber-100 text-amber-700"}`}>
                    {d.severity}
                  </span>
                </td>
                <td className="px-3 py-2.5">{d.reported_date}</td>
                <td className="px-3 py-2.5">
                  <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${d.status === "open" ? "bg-red-100 text-red-700" : d.status === "closed" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                    {d.status}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-[hsl(215,12%,50%)]">{d.location_on_vessel || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // ═════════════════════════════════════════════════════════════════════════
  // ─── MAIN RENDER ─────────────────────────────────────────────────────────
  // ═════════════════════════════════════════════════════════════════════════
  return (
    <PortalLayout>
      <div className="mb-6">
        <p className="text-[11px] font-bold tracking-[3px] uppercase text-[hsl(211,100%,40%)]/60 mb-1">Technical</p>
        {selectedVessel ? (
          <div className="flex items-center gap-2">
            <button onClick={() => { setSelectedVessel(null); setSearch(""); }}
              className="flex items-center gap-1.5 text-sm text-[hsl(215,12%,50%)] hover:text-[hsl(211,100%,40%)] transition-colors">
              <ArrowLeft className="w-4 h-4" /> All Vessels
            </button>
            <ChevronRight className="w-3.5 h-3.5 text-[hsl(215,12%,50%)]" />
            <h1 className="text-2xl font-bold text-[hsl(215,25%,17%)] flex items-center gap-2">
              <Ship className="w-6 h-6 text-[hsl(211,100%,40%)]" /> {selectedVessel.name}
            </h1>
            <Badge variant="outline" className="text-[10px] ml-2">{selectedVessel.type}</Badge>
          </div>
        ) : (
          <h1 className="text-3xl font-bold text-[hsl(215,25%,17%)]">Maintenance & PMS</h1>
        )}
      </div>

      {selectedVessel ? (
        <div>
          <Tabs value={vesselTab} onValueChange={setVesselTab} className="space-y-4">
            <TabsList className="bg-white border border-[hsl(220,15%,90%)]">
              <TabsTrigger value="dashboard" className="flex items-center gap-1.5"><BarChart3 className="w-3.5 h-3.5" /> Dashboard</TabsTrigger>
              <TabsTrigger value="equipment" className="flex items-center gap-1.5"><FolderTree className="w-3.5 h-3.5" /> Equipment</TabsTrigger>
              <TabsTrigger value="schedule" className="flex items-center gap-1.5"><ClipboardList className="w-3.5 h-3.5" /> Maintenance</TabsTrigger>
              <TabsTrigger value="defects" className="flex items-center gap-1.5"><Bug className="w-3.5 h-3.5" /> Defects</TabsTrigger>
              <TabsTrigger value="history" className="flex items-center gap-1.5"><History className="w-3.5 h-3.5" /> Work History</TabsTrigger>
            </TabsList>

            <TabsContent value="dashboard">{renderVesselDashboard()}</TabsContent>
            <TabsContent value="equipment">{renderEquipmentTree()}</TabsContent>
            <TabsContent value="schedule">{renderMaintenanceSchedule()}</TabsContent>
            <TabsContent value="defects">{renderDefects()}</TabsContent>
            <TabsContent value="history">{renderWorkHistory()}</TabsContent>
          </Tabs>
        </div>
      ) : (
        renderVesselGrid()
      )}
    </PortalLayout>
  );
};

export default MaintenancePMS;
